<template>
  <HrPageLayout
    title="Training Applications"
    subtitle="Multi-stage training bio-data applications — committees, bonding letters and reports"
    icon="📋"
    :breadcrumb-items="breadcrumbItems"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-primary" @click="openCreateModal()">
        <i class="bx bx-plus"></i> Create Application
      </button>
      <button type="button" class="hr-btn hr-btn-outline-primary" :disabled="loading" @click="refreshAll">
        <i class="bx" :class="loading ? 'bx-loader-alt bx-spin' : 'bx-refresh'"></i> Refresh
      </button>
      <button type="button" class="hr-btn hr-btn-outline-warning" :disabled="reminding" @click="runReturnReminders">
        <i class="bx bx-bell"></i> {{ reminding ? "Sending…" : "Return-to-work reminders" }}
      </button>
      <button type="button" class="hr-btn hr-btn-outline-success" :disabled="exporting" @click="exportCsv">
        <i class="bx bx-download"></i> CSV
      </button>
      <button type="button" class="hr-btn hr-btn-outline-success" :disabled="exportingPdf" @click="exportPdf">
        <i class="bx" :class="exportingPdf ? 'bx-loader-alt bx-spin' : 'bx-file'"></i> PDF
      </button>
    </template>

    <div class="ta-page">
      <div class="hr-content-grid grid-cards ta-stats">
        <div class="hr-chart-card"><div class="hr-chart-body"><p class="hr-text-muted">Total applications</p><h3>{{ stats.total || 0 }}</h3></div></div>
        <div class="hr-chart-card"><div class="hr-chart-body"><p class="hr-text-muted">Short-term</p><h3>{{ stats.short_term || 0 }}</h3></div></div>
        <div class="hr-chart-card"><div class="hr-chart-body"><p class="hr-text-muted">Long-term</p><h3>{{ stats.long_term || 0 }}</h3></div></div>
        <div class="hr-chart-card"><div class="hr-chart-body"><p class="hr-text-muted">In pipeline</p><h3>{{ stats.in_pipeline || 0 }}</h3></div></div>
        <div class="hr-chart-card"><div class="hr-chart-body"><p class="hr-text-muted">Approved / trained</p><h3>{{ stats.approved || 0 }}</h3></div></div>
        <div class="hr-chart-card"><div class="hr-chart-body"><p class="hr-text-muted">Rejected</p><h3>{{ stats.rejected || 0 }}</h3></div></div>
      </div>

      <div class="hr-chart-card hr-mt-4">
        <div class="hr-chart-header"><h5 class="hr-chart-title"><i class="bx bx-filter"></i> Filters & reports</h5></div>
        <div class="hr-chart-body filter-bar">
          <label class="filter-label">
            <span>Report set</span>
            <select v-model="filters.report" class="filter-select" @change="loadReports">
              <option value="pipeline">Full pipeline</option>
              <option value="approved">Approved / can train</option>
              <option value="rejected">Rejected</option>
              <option value="long_term">Long-term only</option>
              <option value="short_term">Short-term only</option>
              <option value="return_due">Return-to-work due</option>
            </select>
          </label>
          <label class="filter-label">
            <span>Stage (queue)</span>
            <select v-model="filters.stage" class="filter-select" @change="loadQueue">
              <option value="">My stages / all</option>
              <option v-for="s in stageOptions" :key="s.v" :value="s.v">{{ s.l }}</option>
            </select>
          </label>
          <label class="filter-label">
            <span>Term</span>
            <select v-model="filters.training_term" class="filter-select" @change="refreshAll">
              <option value="">All</option>
              <option value="short_term">Short-term (≤90 days)</option>
              <option value="long_term">Long-term (&gt;90 days)</option>
            </select>
          </label>
          <label class="filter-label">
            <span>Course contains</span>
            <input v-model="filters.course" type="text" class="filter-select" placeholder="Course title" @keyup.enter="loadReports">
          </label>
          <label class="filter-label">
            <span>Department</span>
            <input v-model="filters.department_name" type="text" class="filter-select" placeholder="Department" @keyup.enter="refreshAll">
          </label>
          <label class="filter-label">
            <span>Search staff / P.No</span>
            <input v-model="filters.search" type="text" class="filter-select" placeholder="Name or P/No" @keyup.enter="loadQueue">
          </label>
          <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="refreshAll">Apply</button>
        </div>
      </div>

      <div class="hr-chart-card hr-mt-4">
        <div class="hr-chart-header hr-flex-between">
          <h5 class="hr-chart-title"><i class="bx bx-list-check"></i> Review queue</h5>
          <span class="hr-text-muted hr-small">{{ queueMeta.total || 0 }} in view</span>
        </div>
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-wrap"><div class="hr-spinner"></div><p class="hr-text-muted">Loading…</p></div>
          <div v-else class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Officer</th>
                  <th>Course</th>
                  <th>Dept</th>
                  <th>Term</th>
                  <th>Stage</th>
                  <th>Status</th>
                  <th>Path</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in queue" :key="row.id">
                  <td>{{ row.id }}</td>
                  <td>
                    <strong>{{ row.full_name }}</strong>
                    <div class="hr-text-muted hr-small">{{ row.emp_no }}</div>
                  </td>
                  <td>{{ row.course_title }}</td>
                  <td>{{ row.department_name || "—" }}</td>
                  <td>{{ termLabel(row.training_term) }}</td>
                  <td><span class="hr-badge hr-badge-info">{{ stageLabel(row.stage) }}</span></td>
                  <td>{{ row.status }}</td>
                  <td>{{ Number(row.is_health_path) === 1 ? "Health" : "Non-health" }}</td>
                  <td class="hr-d-flex hr-gap-2">
                    <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="openDetail(row)">View</button>
                    <button type="button" v-if="row.stage === 'chrmac_review'" class="hr-btn hr-btn-sm hr-btn-outline-secondary" @click="openCreateModal(row)">Edit</button>
                  </td>
                </tr>
                <tr v-if="!queue.length">
                  <td colspan="9" class="hr-text-center hr-text-muted hr-py-4">No applications in your queues.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="queueMeta.totalPages > 1" class="hr-mt-3 hr-d-flex hr-gap-2">
            <button type="button" class="hr-btn hr-btn-sm" :disabled="!queueMeta.hasPrev" @click="page--; loadQueue()">Prev</button>
            <span class="hr-text-muted">Page {{ queueMeta.page }} / {{ queueMeta.totalPages }}</span>
            <button type="button" class="hr-btn hr-btn-sm" :disabled="!queueMeta.hasNext" @click="page++; loadQueue()">Next</button>
          </div>
        </div>
      </div>

      <div class="hr-chart-card hr-mt-4">
        <div class="hr-chart-header"><h5 class="hr-chart-title"><i class="bx bx-bar-chart-alt-2"></i> Report rows ({{ filters.report }})</h5></div>
        <div class="hr-chart-body">
          <div class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>P/No</th>
                  <th>Course</th>
                  <th>Dates</th>
                  <th>Term</th>
                  <th>Stage</th>
                  <th>Status</th>
                  <th>Dept</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in reportRows" :key="'r' + r.id">
                  <td>{{ r.full_name }}</td>
                  <td>{{ r.emp_no }}</td>
                  <td>{{ r.course_title }}</td>
                  <td>{{ r.course_from || "—" }} → {{ r.course_to || "—" }}</td>
                  <td>{{ termLabel(r.training_term) }}</td>
                  <td>{{ stageLabel(r.stage) }}</td>
                  <td>{{ r.status }}</td>
                  <td>{{ r.department_name }}</td>
                </tr>
                <tr v-if="!reportRows.length">
                  <td colspan="8" class="hr-text-center hr-text-muted hr-py-4">No rows for this report filter.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="(stats.by_course || []).length" class="hr-mt-4">
            <h6>By course</h6>
            <ul class="ta-by-list">
              <li v-for="c in stats.by_course" :key="c.course_title">
                <strong>{{ c.course_title }}</strong> — {{ c.cnt }} apps
                (approved {{ c.approved_cnt }}, LT {{ c.long_term_cnt }}, ST {{ c.short_term_cnt }})
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div v-if="detail" class="hr-modal-overlay" @click.self="closeDetail">
      <div class="hr-modal ta-modal">
        <div class="hr-modal-header">
          <div class="ta-modal-title">
            <span class="ta-modal-eyebrow">Training application #{{ detail.application.id }}</span>
            <h3>{{ detail.application.course_title }}</h3>
          </div>
          <button type="button" class="hr-modal-close" @click="closeDetail">&times;</button>
        </div>
        <div class="ta-detail-body">
          <div class="ta-applicant-summary">
            <div>
              <span class="ta-label">Applicant</span>
              <strong>{{ detail.application.full_name || '—' }}</strong>
            </div>
            <div>
              <span class="ta-label">P/No</span>
              <strong>{{ detail.application.emp_no || '—' }}</strong>
            </div>
            <div class="ta-summary-wide">
              <span class="ta-label">Department</span>
              <strong>{{ detail.application.department_name || '—' }}</strong>
            </div>
          </div>

          <div class="ta-fact-grid">
            <div class="ta-fact"><span>Stage</span><strong><span class="hr-badge hr-badge-info">{{ stageLabel(detail.application.stage) }}</span></strong></div>
            <div class="ta-fact"><span>Status</span><strong>{{ detail.application.status || '—' }}</strong></div>
            <div class="ta-fact"><span>Path</span><strong>{{ Number(detail.application.is_health_path) === 1 ? "Health" : "Departmental" }}</strong></div>
            <div v-if="detail.application.course_from" class="ta-fact"><span>Duration</span><strong>{{ detail.application.course_from }} → {{ detail.application.course_to }}</strong><small>{{ termLabel(detail.application.training_term) }}, {{ detail.application.duration_days || "—" }} days</small></div>
            <div class="ta-fact"><span>Venue / mode</span><strong>{{ detail.application.venue || "—" }}</strong><small>{{ detail.application.mode_of_study || "Mode not specified" }}</small></div>
            <div class="ta-fact"><span>Sponsor / cost</span><strong>{{ detail.application.sponsor || "—" }}</strong><small>{{ detail.application.total_cost != null ? "Ksh " + detail.application.total_cost : "Cost not specified" }}</small></div>
          </div>

          <div v-if="detail.application.supervisor_justification" class="ta-detail-section">
            <h4>Supervisor justification</h4>
            <p class="ta-pre">{{ detail.application.supervisor_justification }}</p>
          </div>
          <div v-if="detail.application.authorized_officer_justification" class="ta-detail-section">
            <h4>Authorized officer</h4>
            <p class="ta-pre">{{ detail.application.authorized_officer_justification }}</p>
          </div>
          <div v-if="detail.application.rejection_reason" class="hr-alert hr-alert-warning">
            Rejected: {{ detail.application.rejection_reason }}
          </div>

          <div class="ta-detail-section">
            <h4>Documents</h4>
            <ul v-if="detail.docs && detail.docs.length" class="ta-docs">
              <li v-for="d in detail.docs" :key="d.id">
                <div><span class="ta-doc-type">{{ d.doc_type }}</span> <span class="hr-text-muted">{{ d.stage }}</span></div>
                <a href="#" @click.prevent="viewDocument(d.file_path)">{{ d.original_name || d.file_path }}</a>
                <span class="hr-text-muted hr-small">Uploaded by {{ d.uploaded_by || '—' }}</span>
              </li>
            </ul>
            <p v-else class="hr-text-muted">No documents yet.</p>
          </div>

          <div class="ta-detail-section">
            <h4>Timeline</h4>
            <ul class="ta-log">
              <li v-for="l in detail.log" :key="l.id">
                <span class="hr-text-muted">{{ l.created_at }}</span> — {{ l.action }}
                <span v-if="l.from_stage"> ({{ l.from_stage }} → {{ l.to_stage }})</span>
                <span v-if="l.details">: {{ l.details }}</span>
              </li>
            </ul>
          </div>

          <div class="ta-actions">
            <label class="filter-label ta-comments">
              <span>Comments / rejection reason</span>
              <textarea v-model="actionComments" rows="2" class="filter-select" placeholder="Optional notes or required rejection reason"></textarea>
            </label>

            <div v-if="isReviewStage" class="hr-d-flex hr-gap-2 hr-flex-wrap">
              <button type="button" class="hr-btn hr-btn-success" :disabled="acting" @click="doAction('approve_review')">
                Pass review → next stage
              </button>
              <button type="button" class="hr-btn hr-btn-danger" :disabled="acting" @click="doAction('reject')">Reject</button>
            </div>

            <div v-if="isDocsStage" class="hr-mt-2">
              <p class="hr-text-muted hr-small">Upload both minutes and committee report, then forward.</p>
              <div class="ta-upload-row">
                <label>
                  Minutes
                  <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" @change="onFile($event, 'minutes')" />
                </label>
                <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" :disabled="!pendingFiles.minutes || uploading" @click="uploadDoc('minutes')">
                  Upload minutes
                </button>
              </div>
              <div class="ta-upload-row">
                <label>
                  Report
                  <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" @change="onFile($event, 'report')" />
                </label>
                <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" :disabled="!pendingFiles.report || uploading" @click="uploadDoc('report')">
                  Upload report
                </button>
              </div>
              <div class="hr-d-flex hr-gap-2 hr-mt-2">
                <button type="button" class="hr-btn hr-btn-primary" :disabled="acting" @click="doAction('forward_after_docs')">
                  Forward with minutes & report
                </button>
                <button type="button" class="hr-btn hr-btn-danger" :disabled="acting" @click="doAction('reject')">Reject</button>
              </div>
            </div>

            <div v-if="isBondingStage" class="hr-mt-2">
              <p class="hr-text-muted">Head of Training: upload bonding letter — employee is notified to collect it and proceed to training.</p>
              <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" @change="onFile($event, 'bonding')" />
              <button type="button" class="hr-btn hr-btn-success hr-mt-2" :disabled="!pendingFiles.bonding || uploading" @click="uploadBonding">
                Upload bonding letter & approve
              </button>
            </div>
            <p v-if="actionMsg" class="hr-mt-2" :class="actionOk ? 'hr-text-success' : 'hr-text-danger'">{{ actionMsg }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Application Modal -->
    <div v-if="showCreateModal" class="hr-modal-overlay" @click.self="closeCreateModal">
      <div class="hr-modal ta-modal">
        <div class="hr-modal-header">
          <h3>{{ createForm.id ? 'Edit Application' : 'Create Application (on behalf)' }}</h3>
          <button type="button" class="hr-modal-close" @click="closeCreateModal">&times;</button>
        </div>
        <div class="ta-create-body">
          <div v-if="!createForm.id" class="ta-create-search">
            <div class="ta-create-section-heading"><span class="ta-create-step">1</span><div><h4>Select active employee</h4><p>Search the active employee register before completing the application.</p></div></div>
            <div class="ta-search-line">
              <input v-model="searchKeyword" type="search" class="ta-create-input" placeholder="Search by name, P/No or national ID" @keyup.enter="searchStaff">
              <button type="button" class="hr-btn hr-btn-outline-primary" @click="searchStaff" :disabled="searching || searchKeyword.trim().length < 2"><i class="bx" :class="searching ? 'bx-loader-alt bx-spin' : 'bx-search'"></i>{{ searching ? 'Searching...' : 'Search' }}</button>
            </div>
            <ul v-if="searchResults.length" class="staff-search-results">
              <li v-for="emp in searchResults" :key="emp.id" @click="selectStaff(emp)">
                <span class="ta-result-avatar">{{ getEmployeeInitials(emp) }}</span>
                <span><strong>{{ employeeDisplayName(emp) }}</strong><small>{{ emp.empNo || emp.emp_no || '—' }} · {{ emp.departmentName || 'Department not set' }}</small></span>
                <i class="bx bx-chevron-right"></i>
              </li>
            </ul>
            <p v-else-if="searchKeyword.length >= 2 && !searching" class="ta-search-empty">No active employees found.</p>
          </div>

          <div v-if="selectedEmployee" class="ta-create-form">
            <div class="ta-selected-employee"><span class="ta-result-avatar">{{ getEmployeeInitials(selectedEmployee) }}</span><div><span>Applying on behalf of</span><strong>{{ employeeDisplayName(selectedEmployee) }}</strong><small>{{ selectedEmployee.empNo || selectedEmployee.emp_no || '—' }} · {{ selectedEmployee.departmentName || 'Department not set' }}</small></div><button v-if="!createForm.id" type="button" class="ta-change-btn" @click="selectedEmployee = null">Change</button></div>

            <div class="ta-create-section-heading"><span class="ta-create-step">2</span><div><h4>Training application details</h4><p>Complete the same course and bond information used in employee self-service.</p></div></div>
            <section class="ta-create-section"><h5>Previous course and bond</h5><div class="ta-create-grid">
              <label class="ta-create-field ta-span-2"><span>Title of last course attended</span><input v-model="createForm.last_course_title" type="text" class="ta-create-input"></label>
              <label class="ta-create-field"><span>Last course from</span><input v-model="createForm.last_course_from" type="date" class="ta-create-input"></label>
              <label class="ta-create-field"><span>Last course to</span><input v-model="createForm.last_course_to" type="date" class="ta-create-input"></label>
              <label class="ta-create-field"><span>Bond period</span><input v-model="createForm.last_bond_period" type="text" class="ta-create-input"></label>
              <label class="ta-create-field"><span>Bond amount (Ksh)</span><input v-model="createForm.last_bond_amount" type="number" min="0" step="0.01" class="ta-create-input"></label>
              <label class="ta-create-field"><span>Date bond expired</span><input v-model="createForm.last_bond_expired" type="date" class="ta-create-input"></label>
            </div></section>

            <section class="ta-create-section ta-create-section-accent"><h5>Course requested</h5><div class="ta-create-grid">
              <label class="ta-create-field ta-span-2"><span>Title of course requested <em>*</em></span><input v-model="createForm.course_title" type="text" required class="ta-create-input" placeholder="Full name of the course"></label>
              <label class="ta-create-field"><span>Duration from</span><input v-model="createForm.course_from" type="date" class="ta-create-input"></label>
              <label class="ta-create-field"><span>Duration to</span><input v-model="createForm.course_to" type="date" class="ta-create-input"></label>
              <label class="ta-create-field"><span>Mode of study</span><select v-model="createForm.mode_of_study" class="ta-create-input"><option value="">Select...</option><option>Full-time</option><option>Part-time</option><option>Online / distance</option><option>Block release</option><option>Sandwich</option></select></label>
              <label class="ta-create-field"><span>Venue</span><input v-model="createForm.venue" type="text" class="ta-create-input" placeholder="Institution / location"></label>
              <label class="ta-create-field"><span>Sponsor</span><input v-model="createForm.sponsor" type="text" class="ta-create-input" placeholder="Self, County, partner..."></label>
              <label class="ta-create-field"><span>Total training cost (Ksh)</span><input v-model="createForm.total_cost" type="number" min="0" step="0.01" class="ta-create-input"></label>
              <label class="ta-create-field ta-span-2"><span>Applicant signature</span><input v-model="createForm.applicant_signature" type="text" class="ta-create-input" placeholder="Type applicant's full name"></label>
            </div></section>

            <div class="ta-create-upload"><div><strong>Supporting document</strong><small>Optional PDF, Word document or image.</small></div><input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" @change="onCreateFile"></div>
            <div class="ta-create-footer"><span v-if="createSubmitMessage" :class="createSubmitOk ? 'hr-text-success' : 'hr-text-danger'">{{ createSubmitMessage }}</span><button type="button" class="hr-btn hr-btn-primary" @click="submitProxyApplication" :disabled="submittingProxy || !createForm.course_title"><i class="bx" :class="submittingProxy ? 'bx-loader-alt bx-spin' : 'bx-send'"></i>{{ submittingProxy ? 'Submitting...' : (createForm.id ? 'Update & Submit' : 'Submit Application') }}</button></div>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { addCountyLetterhead, loadCountyLogoDataUrl } from "@/utils/payrollReconPdf";

const STAGE_LABELS = {
  draft: "Draft",
  sub_county_review: "Sub-county HR Committee",
  sub_county_docs: "Sub-county — minutes/report",
  departmental_review: "Departmental HR Committee",
  departmental_docs: "Dept HR — minutes/report",
  chrmac_review: "CHRMAC",
  chrmac_docs: "Head of Training → PSB package",
  psb_ceo_review: "PSB CEO",
  bonding_upload: "Bonding letter",
  approved: "Approved — proceed",
  rejected: "Rejected",
  in_training: "In training",
  completed: "Completed",
};

export default {
  name: "TrainingApplications",
  components: { HrPageLayout },
  data() {
    return {
      breadcrumbItems: [
        { label: "Home", to: "/" },
        { label: "Training Management", to: "/training-management" },
        { label: "Applications" },
      ],
      loading: false,
      reminding: false,
      exporting: false,
      exportingPdf: false,
      acting: false,
      uploading: false,
      letterhead: null,
      stats: {},
      queue: [],
      queueMeta: {},
      reportRows: [],
      page: 1,
      filters: {
        report: "pipeline",
        stage: "",
        training_term: "",
        course: "",
        department_name: "",
        search: "",
      },
      stageOptions: Object.keys(STAGE_LABELS).map((v) => ({ v, l: STAGE_LABELS[v] })),
      detail: null,
      actionComments: "",
      actionMsg: "",
      actionOk: false,
      pendingFiles: { minutes: null, report: null, bonding: null },
      showCreateModal: false,
      searchKeyword: "",
      searchResults: [],
      searching: false,
      selectedEmployee: null,
      createForm: {
        id: 0, last_course_title: "", last_course_from: "", last_course_to: "", last_bond_period: "", last_bond_amount: "", last_bond_expired: "",
        course_title: "", course_from: "", course_to: "", training_term: "", mode_of_study: "", venue: "", sponsor: "", total_cost: "", applicant_signature: ""
      },
      createDocument: null,
      submittingProxy: false,
      createSubmitMessage: "",
      createSubmitOk: false,
    };
  },
  computed: {
    stage() {
      return this.detail?.application?.stage || "";
    },
    isReviewStage() {
      return ["sub_county_review", "departmental_review", "chrmac_review", "psb_ceo_review"].includes(this.stage);
    },
    isDocsStage() {
      return ["sub_county_docs", "departmental_docs", "chrmac_docs"].includes(this.stage);
    },
    isBondingStage() {
      return this.stage === "bonding_upload";
    },
  },
  mounted() {
    this.refreshAll();
  },
  methods: {
    stageLabel(s) {
      return STAGE_LABELS[s] || s || "—";
    },
    termLabel(t) {
      if (t === "long_term") return "Long-term";
      if (t === "short_term") return "Short-term";
      return t || "—";
    },
    viewDocument(path) {
      if (path) window.open('/' + path, '_blank');
    },
    openCreateModal(row = null) {
      this.showCreateModal = true;
      this.searchKeyword = "";
      this.searchResults = [];
      this.selectedEmployee = null;
      this.createDocument = null;
      this.createSubmitMessage = "";
      this.createSubmitOk = false;
      if (row) {
        this.selectedEmployee = { id: row.employee_id, full_name: row.full_name, emp_no: row.emp_no, departmentName: row.department_name };
        this.createForm = {
          id: row.id,
          last_course_title: row.last_course_title || "",
          last_course_from: row.last_course_from || "",
          last_course_to: row.last_course_to || "",
          last_bond_period: row.last_bond_period || "",
          last_bond_amount: row.last_bond_amount || "",
          last_bond_expired: row.last_bond_expired || "",
          course_title: row.course_title || "",
          course_from: row.course_from || "",
          course_to: row.course_to || "",
          training_term: row.training_term || "",
          mode_of_study: row.mode_of_study || "",
          venue: row.venue || "",
          sponsor: row.sponsor || "",
          total_cost: row.total_cost || "",
          applicant_signature: row.applicant_signature || ""
        };
      } else {
        this.createForm = { id: 0, last_course_title: "", last_course_from: "", last_course_to: "", last_bond_period: "", last_bond_amount: "", last_bond_expired: "", course_title: "", course_from: "", course_to: "", training_term: "", mode_of_study: "", venue: "", sponsor: "", total_cost: "", applicant_signature: "" };
      }
    },
    closeCreateModal() {
      this.showCreateModal = false;
    },
    async searchStaff() {
      if (!this.searchKeyword.trim()) return;
      this.searching = true;
      try {
        const fd = new FormData();
        fd.append("function", "getEmployees");
        fd.append("keyword", this.searchKeyword.trim());
        fd.append("status", "Active");
        fd.append("page", "1");
        fd.append("rows_per_page", "25");
        const res = await execute(fd, authUrl);
        this.searchResults = res.data?.success ? (res.data?.data?.employees || []) : [];
      } catch (e) {
        console.error(e);
      } finally {
        this.searching = false;
      }
    },
    selectStaff(emp) {
      this.selectedEmployee = emp;
      this.searchResults = [];
      this.createForm.applicant_signature = this.employeeDisplayName(emp);
    },
    employeeDisplayName(emp) {
      return emp?.full_name || emp?.employeeName || [emp?.firstName, emp?.middleName, emp?.lastName].filter(Boolean).join(" ") || "Unnamed employee";
    },
    getEmployeeInitials(emp) {
      return this.employeeDisplayName(emp).split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase();
    },
    onCreateFile(ev) {
      this.createDocument = ev.target.files?.[0] || null;
    },
    async submitProxyApplication() {
      if (!this.selectedEmployee || !this.createForm.course_title) return;
      this.submittingProxy = true;
      this.createSubmitMessage = "";
      try {
        const fd = new FormData();
        fd.append("function", "taSubmitApplication");
        fd.append("submit", "1");
        fd.append("employee_id", this.selectedEmployee.id);
        Object.keys(this.createForm).forEach(k => fd.append(k, this.createForm[k] || ""));
        if (this.createDocument) {
          fd.append("document", this.createDocument);
        }
        const res = await execute(fd, authUrl);
        if (res.data?.success) {
          this.createSubmitOk = true;
          this.createSubmitMessage = "Application submitted on behalf of the selected employee.";
          this.closeCreateModal();
          await this.refreshAll();
        } else {
          this.createSubmitOk = false;
          this.createSubmitMessage = res.data?.message || "Failed to submit";
        }
      } catch (e) {
        this.createSubmitOk = false;
        this.createSubmitMessage = e?.message || "Error submitting application";
      } finally {
        this.submittingProxy = false;
      }
    },
    appendFilters(fd, includeSearch) {
      Object.keys(this.filters).forEach((k) => {
        if (k === "search" && !includeSearch) return;
        if (this.filters[k]) fd.append(k, this.filters[k]);
      });
    },
    async refreshAll() {
      await Promise.all([this.loadQueue(), this.loadReports()]);
    },
    async loadQueue() {
      this.loading = true;
      try {
        const fd = new FormData();
        fd.append("function", "taListQueue");
        fd.append("page", String(this.page));
        fd.append("limit", "25");
        if (this.filters.stage) fd.append("stage", this.filters.stage);
        if (this.filters.training_term) fd.append("training_term", this.filters.training_term);
        if (this.filters.department_name) fd.append("department_name", this.filters.department_name);
        if (this.filters.search) fd.append("search", this.filters.search);
        const res = await execute(fd, authUrl);
        this.queue = res.data?.success ? (res.data.data?.applications || []) : [];
        this.queueMeta = res.data?.data?.pagination || {};
      } catch (_) {
        this.queue = [];
      } finally {
        this.loading = false;
      }
    },
    async loadReports() {
      try {
        const fd = new FormData();
        fd.append("function", "taGetReports");
        this.appendFilters(fd, false);
        const res = await execute(fd, authUrl);
        if (res.data?.success) {
          this.stats = res.data.data?.stats || {};
          this.reportRows = res.data.data?.rows || [];
        }
      } catch (_) {
        this.reportRows = [];
      }
    },
    async openDetail(row) {
      this.actionMsg = "";
      this.actionComments = "";
      this.pendingFiles = { minutes: null, report: null, bonding: null };
      try {
        const fd = new FormData();
        fd.append("function", "taGetApplication");
        fd.append("id", row.id);
        const res = await execute(fd, authUrl);
        if (res.data?.success) {
          this.detail = {
            application: res.data.data.application,
            docs: res.data.data.docs || [],
            log: res.data.data.log || [],
          };
        }
      } catch (e) {
        alert(e?.message || "Could not load application");
      }
    },
    closeDetail() {
      this.detail = null;
    },
    onFile(ev, key) {
      this.pendingFiles[key] = ev.target.files?.[0] || null;
    },
    async doAction(action) {
      if (!this.detail) return;
      if (action === "reject" && !this.actionComments.trim()) {
        this.actionOk = false;
        this.actionMsg = "Enter a rejection reason.";
        return;
      }
      this.acting = true;
      this.actionMsg = "";
      try {
        const fd = new FormData();
        fd.append("function", "taCommitteeAction");
        fd.append("id", this.detail.application.id);
        fd.append("action", action);
        fd.append("comments", this.actionComments);
        const res = await execute(fd, authUrl);
        this.actionOk = !!res.data?.success;
        this.actionMsg = res.data?.message || (this.actionOk ? "Done" : "Failed");
        if (this.actionOk) {
          await this.openDetail(this.detail.application);
          await this.refreshAll();
        }
      } catch (e) {
        this.actionOk = false;
        this.actionMsg = e?.message || "Action failed";
      } finally {
        this.acting = false;
      }
    },
    async uploadDoc(docType) {
      const file = this.pendingFiles[docType];
      if (!file || !this.detail) return;
      this.uploading = true;
      this.actionMsg = "";
      try {
        const fd = new FormData();
        fd.append("function", "taUploadStageDocs");
        fd.append("id", this.detail.application.id);
        fd.append("doc_type", docType);
        fd.append("file", file);
        const res = await execute(fd, authUrl);
        this.actionOk = !!res.data?.success;
        this.actionMsg = res.data?.message || "Uploaded";
        if (this.actionOk) {
          this.pendingFiles[docType] = null;
          await this.openDetail(this.detail.application);
        }
      } catch (e) {
        this.actionOk = false;
        this.actionMsg = e?.message || "Upload failed";
      } finally {
        this.uploading = false;
      }
    },
    async uploadBonding() {
      const file = this.pendingFiles.bonding;
      if (!file || !this.detail) return;
      this.uploading = true;
      try {
        const fd = new FormData();
        fd.append("function", "taUploadBondingLetter");
        fd.append("id", this.detail.application.id);
        fd.append("file", file);
        const res = await execute(fd, authUrl);
        this.actionOk = !!res.data?.success;
        this.actionMsg = res.data?.message || "Done";
        if (this.actionOk) {
          await this.openDetail(this.detail.application);
          await this.refreshAll();
        }
      } catch (e) {
        this.actionOk = false;
        this.actionMsg = e?.message || "Upload failed";
      } finally {
        this.uploading = false;
      }
    },
    async runReturnReminders() {
      this.reminding = true;
      try {
        const fd = new FormData();
        fd.append("function", "taProcessReturnReminders");
        const res = await execute(fd, authUrl);
        alert(res.data?.message || "Done");
        await this.refreshAll();
      } catch (e) {
        alert(e?.message || "Failed");
      } finally {
        this.reminding = false;
      }
    },
    exportCsv() {
      this.exporting = true;
      try {
        const headers = ["ID", "Name", "P/No", "Course", "From", "To", "Term", "Stage", "Status", "Department", "Path"];
        const lines = [headers.join(",")];
        (this.reportRows || []).forEach((r) => {
          const cells = [
            r.id,
            r.full_name,
            r.emp_no,
            r.course_title,
            r.course_from,
            r.course_to,
            r.training_term,
            r.stage,
            r.status,
            r.department_name,
            Number(r.is_health_path) === 1 ? "Health" : "Non-health",
          ].map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`);
          lines.push(cells.join(","));
        });
        const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `training-applications-${this.filters.report}-${Date.now()}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      } finally {
        this.exporting = false;
      }
    },
    async exportPdf() {
      this.exportingPdf = true;
      try {
        const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
        const logoDataUrl = await loadCountyLogoDataUrl(this.letterhead);
        const startY = addCountyLetterhead(doc, {
          logoDataUrl,
          title: "Training Applications Report",
          subtitle: `Report: ${this.filters.report} · Generated ${new Date().toLocaleString()}`,
        });
        const body = (this.reportRows || []).map((r) => [
          r.full_name || "",
          r.emp_no || "",
          r.course_title || "",
          `${r.course_from || ""} – ${r.course_to || ""}`,
          this.termLabel(r.training_term),
          this.stageLabel(r.stage),
          r.status || "",
          r.department_name || "",
        ]);
        autoTable(doc, {
          startY: startY + 8,
          head: [["Name", "P/No", "Course", "Dates", "Term", "Stage", "Status", "Dept"]],
          body,
          styles: { fontSize: 8, cellPadding: 3 },
          headStyles: { fillColor: [15, 118, 110] },
          margin: { left: 36, right: 36 },
        });
        let y = doc.lastAutoTable.finalY + 16;
        doc.setFontSize(10);
        doc.text(
          `Summary — Total: ${this.stats.total || 0} | Short: ${this.stats.short_term || 0} | Long: ${this.stats.long_term || 0} | Approved: ${this.stats.approved || 0} | Pipeline: ${this.stats.in_pipeline || 0}`,
          36,
          y
        );
        doc.save(`training-applications-${this.filters.report}.pdf`);
      } catch (e) {
        alert(e?.message || "PDF export failed");
      } finally {
        this.exportingPdf = false;
      }
    },
  },
};
</script>

<style scoped>
.ta-stats { grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); }
.filter-bar { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.filter-label { display: flex; flex-direction: column; gap: 4px; font-size: 0.8rem; font-weight: 600; color: #334155; }
.filter-select { min-width: 140px; padding: 6px 10px; border: 1px solid #cbd5e1; border-radius: 6px; font-weight: 400; }
.ta-modal { width: min(820px, 96vw); max-height: 90vh; overflow: hidden; background: #fff; color: #0f172a; border: 1px solid #dbe4ee; border-radius: 14px; box-shadow: 0 24px 70px rgba(15, 23, 42, .3); }
.ta-modal-title { min-width: 0; padding-right: 12px; }
.ta-modal-title h3 { margin: 3px 0 0; font-size: 1.15rem; line-height: 1.35; overflow-wrap: anywhere; }
.ta-modal-eyebrow { color: #64748b; font-size: .72rem; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
.ta-detail-body { max-height: calc(90vh - 76px); overflow-y: auto; padding: 22px 24px 26px; }
.ta-applicant-summary { display: grid; grid-template-columns: minmax(0, 1.5fr) minmax(110px, .7fr) minmax(0, 1.2fr); gap: 12px; padding: 16px; margin-bottom: 18px; border: 1px solid #dbe4ee; border-radius: 12px; background: #f8fafc; }
.ta-applicant-summary > div { min-width: 0; }
.ta-label, .ta-fact span { display: block; margin-bottom: 5px; color: #64748b; font-size: .72rem; font-weight: 700; letter-spacing: .03em; text-transform: uppercase; }
.ta-applicant-summary strong, .ta-fact strong { display: block; overflow-wrap: anywhere; }
.ta-fact-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-bottom: 20px; }
.ta-fact { min-width: 0; padding: 13px 14px; border: 1px solid #e2e8f0; border-radius: 10px; background: #fff; }
.ta-fact small { display: block; margin-top: 4px; color: #64748b; overflow-wrap: anywhere; }
.ta-detail-section { margin-top: 18px; padding-top: 18px; border-top: 1px solid #e2e8f0; }
.ta-detail-section h4 { margin: 0 0 9px; color: #334155; font-size: .9rem; }
.ta-pre { white-space: pre-wrap; overflow-wrap: anywhere; margin: 0; background: #f8fafc; padding: 12px 14px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 0.9rem; line-height: 1.55; }
.ta-docs, .ta-log, .ta-by-list { margin: 8px 0 0; padding-left: 0; list-style: none; }
.ta-docs li { display: flex; flex-wrap: wrap; align-items: baseline; gap: 5px 10px; padding: 10px 0; border-bottom: 1px solid #eef2f7; overflow-wrap: anywhere; }
.ta-docs li:last-child { border-bottom: 0; }
.ta-docs a { color: #0f766e; font-weight: 700; overflow-wrap: anywhere; }
.ta-doc-type { font-weight: 700; color: #334155; }
.ta-create-body { max-height: calc(90vh - 76px); overflow-y: auto; padding: 22px 24px 26px; background: #f8fafc; }
.ta-create-search, .ta-create-form { max-width: 760px; margin: 0 auto; }
.ta-create-search { padding: 18px; border: 1px solid #dbe4ee; border-radius: 12px; background: #fff; }
.ta-create-section-heading { display: flex; gap: 11px; align-items: flex-start; margin-bottom: 14px; }
.ta-create-section-heading h4 { margin: 0; color: #0f172a; font-size: .98rem; }
.ta-create-section-heading p { margin: 3px 0 0; color: #64748b; font-size: .82rem; line-height: 1.45; }
.ta-create-step { display: inline-flex; width: 25px; height: 25px; flex: 0 0 25px; align-items: center; justify-content: center; border-radius: 50%; background: #0f766e; color: #fff; font-size: .78rem; font-weight: 800; }
.ta-search-line { display: flex; gap: 9px; }
.ta-create-input { width: 100%; min-width: 0; padding: 9px 11px; border: 1px solid #cbd5e1; border-radius: 8px; background: #fff; color: #0f172a; font: inherit; }
.ta-create-input:focus { outline: 2px solid rgba(15, 118, 110, .18); border-color: #0f766e; }
.ta-search-line .ta-create-input { flex: 1; }
.ta-search-empty { margin: 13px 0 0; color: #64748b; font-size: .85rem; }
.ta-create-form { display: flex; flex-direction: column; gap: 16px; }
.ta-selected-employee { display: flex; align-items: center; gap: 11px; padding: 13px 15px; border: 1px solid #99f6e4; border-radius: 12px; background: #f0fdfa; }
.ta-selected-employee > div { min-width: 0; flex: 1; }
.ta-selected-employee span, .ta-selected-employee small { display: block; color: #0f766e; font-size: .76rem; }
.ta-selected-employee strong { display: block; color: #134e4a; overflow-wrap: anywhere; }
.ta-selected-employee small { color: #64748b; margin-top: 2px; }
.ta-result-avatar { display: inline-flex; width: 36px; height: 36px; flex: 0 0 36px; align-items: center; justify-content: center; border-radius: 50%; background: #ccfbf1; color: #0f766e; font-size: .78rem; font-weight: 800; }
.ta-change-btn { border: 0; background: transparent; color: #0f766e; font-weight: 700; cursor: pointer; }
.ta-create-section { padding: 16px; border: 1px solid #e2e8f0; border-radius: 12px; background: #fff; }
.ta-create-section-accent { border-color: #99f6e4; box-shadow: inset 3px 0 #0f766e; }
.ta-create-section h5 { margin: 0 0 13px; color: #334155; font-size: .9rem; }
.ta-create-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.ta-create-field { display: flex; flex-direction: column; gap: 5px; min-width: 0; color: #334155; font-size: .78rem; font-weight: 700; }
.ta-create-field em { color: #dc2626; font-style: normal; }
.ta-create-field .ta-create-input { font-weight: 400; }
.ta-span-2 { grid-column: span 2; }
.ta-create-upload { display: flex; justify-content: space-between; align-items: center; gap: 15px; padding: 14px 16px; border: 1px dashed #94a3b8; border-radius: 10px; background: #fff; }
.ta-create-upload strong, .ta-create-upload small { display: block; }
.ta-create-upload small { margin-top: 3px; color: #64748b; font-size: .78rem; }
.ta-create-footer { display: flex; align-items: center; justify-content: flex-end; gap: 14px; padding-top: 2px; }
.ta-create-footer > span { margin-right: auto; font-size: .82rem; }
.ta-log li { position: relative; margin: 0 0 10px 8px; padding: 0 0 0 16px; border-left: 2px solid #cbd5e1; overflow-wrap: anywhere; line-height: 1.5; }
.ta-log li:last-child { margin-bottom: 0; }
.ta-actions { margin-top: 20px; padding: 16px; border: 1px solid #dbe4ee; border-radius: 12px; background: #f8fafc; }
.ta-upload-row { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin: 8px 0; }
.ta-comments { width: 100%; }
.ta-comments .filter-select { width: 100%; min-height: 60px; }
.hr-flex-between { display: flex; justify-content: space-between; align-items: center; }
.hr-d-flex { display: flex; }
.hr-flex-wrap { flex-wrap: wrap; }
.hr-gap-2 { gap: 8px; }
.hr-gap-3 { gap: 12px; }
.hr-flex-1 { flex: 1; }
.staff-search-results { list-style: none; padding: 0; margin: 0; border: 1px solid #cbd5e1; border-radius: 6px; max-height: 200px; overflow-y: auto; }
.staff-search-results li { padding: 8px 12px; border-bottom: 1px solid #e2e8f0; cursor: pointer; }
.staff-search-results li:hover { background: #f1f5f9; }
.staff-search-results li:last-child { border-bottom: none; }
.staff-search-results li { display: flex; align-items: center; gap: 10px; padding: 11px 12px; }
.staff-search-results li > span:nth-child(2) { min-width: 0; flex: 1; }
.staff-search-results li strong, .staff-search-results li small { display: block; overflow-wrap: anywhere; }
.staff-search-results li small { margin-top: 3px; color: #64748b; }
.staff-search-results li > i { color: #94a3b8; font-size: 1.1rem; }
@media (max-width: 640px) {
  .ta-detail-body { padding: 16px; }
  .ta-applicant-summary, .ta-fact-grid { grid-template-columns: 1fr; }
  .ta-summary-wide { grid-column: auto; }
  .ta-modal-title h3 { font-size: 1rem; }
  .ta-create-body { padding: 15px; }
  .ta-search-line, .ta-create-upload { align-items: stretch; flex-direction: column; }
  .ta-create-grid { grid-template-columns: 1fr; }
  .ta-span-2 { grid-column: auto; }
  .ta-create-footer { align-items: stretch; flex-direction: column; }
  .ta-create-footer > span { margin-right: 0; }
}
</style>
